import { memo, useState, useRef, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import Dropdown from 'rc-dropdown'
import SaveBackgroundButton from './SaveBackgroundButton'
import { addVirtualBackground, resetAddImage } from '../vb/actions.js'
import { selectQuery } from './reducer.js'
import { setIsNotificationVisible } from './actions.js'
import { logResultImageClicked } from '../telemetry/telemetry.js'
import ellipsisIcon from './assets/ellipsis-icon.png'
import 'rc-dropdown/assets/index.css'
import './NewGeneratedImage.css'

const IS_TOUCH_DEVICE =
  typeof window !== 'undefined' &&
  (window.matchMedia?.('(pointer: coarse)')?.matches ||
    navigator.maxTouchPoints > 0)

const NewGeneratedImage = ({
  effectiveSelectedImageId,
  image,
  index,
  onSelectImage,
  selectionId,
  total,
}) => {
  const [isHover, setIsHover] = useState(false)
  const [isImageLoaded, setIsImageLoaded] = useState(false)
  const dispatch = useDispatch()
  const isSelected = selectionId === effectiveSelectedImageId
  const showEllipsisButton =
    (!IS_TOUCH_DEVICE && isHover && isImageLoaded) ||
    (IS_TOUCH_DEVICE && isSelected && isImageLoaded)
  const imageRef = useRef(null)
  const query = useSelector(selectQuery)
  const queryLabel = (query || '').trim()

  useEffect(() => {
    if (!IS_TOUCH_DEVICE && isSelected && imageRef.current) {
      imageRef.current.focus()
    }
  }, [isSelected])

  useEffect(() => {
    if (!IS_TOUCH_DEVICE && index === 0) {
      imageRef.current?.focus()
    }
  }, [index])

  const ellipsisRef = useRef(null)

  const handleMouseEnter = () => {
    if (IS_TOUCH_DEVICE) {
      return
    }

    setIsHover(true)
  }

  const handleMouseLeave = () => {
    if (IS_TOUCH_DEVICE) {
      return
    }

    setIsHover(false)
  }

  const handleSetVirtualBackground = (e) => {
    if (!isImageLoaded) {
      return
    }

    if (ellipsisRef.current?.contains(e.target)) {
      return
    }

    if (imageRef.current.contains(e.target)) {
      onSelectImage(image, selectionId)
      logResultImageClicked()
    }
  }

  const handleSaveVirtualBackground = () => {
    dispatch(setIsNotificationVisible(false))
    dispatch(resetAddImage())
    dispatch(addVirtualBackground(image))
  }

  const handleEllipsisClick = (e) => {
    e.stopPropagation()
  }

  const handleKeyDown = (e) => {
    if (
      e.key === 'ArrowUp' ||
      e.key === 'ArrowDown' ||
      e.key === 'ArrowLeft' ||
      e.key === 'ArrowRight'
    ) {
      e.preventDefault()
      const direction = e.key === 'ArrowUp' || e.key === 'ArrowLeft' ? -1 : 1
      const images = document.querySelectorAll('.new-generated-image-container')
      const currentIndex = Array.from(images).indexOf(imageRef.current)
      const newIndex = currentIndex + direction
      if (images[newIndex]) {
        images[newIndex]?.focus()
      }
    } else if (e.key === 'Enter') {
      e.preventDefault()
      handleSetVirtualBackground(e)
    }
  }

  return (
    <div
      ref={imageRef}
      className={`new-generated-image-container ${
        !isImageLoaded ? 'disabled' : ''
      } ${isSelected ? 'selected' : ''} ${
        index + 1 === total ? 'last-generated-image' : ''
      }`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={handleSetVirtualBackground}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      aria-disabled={!isImageLoaded || isSelected}
    >
      <img
        className="new-generated-image"
        onLoad={() => setIsImageLoaded(true)}
        src={image.src.dataUrl}
        alt={selectionId}
        aria-label={`Results, grouping, ${queryLabel}, button, ${
          isSelected ? 'selected' : 'not selected'
        }, ${index + 1} of ${total}`}
      />

      {showEllipsisButton && (
        <Dropdown
          trigger={['click']}
          overlay={
            <SaveBackgroundButton
              setIsHover={setIsHover}
              handleSaveVirtualBackground={handleSaveVirtualBackground}
            />
          }
          placement="bottomRight"
        >
          <button
            ref={ellipsisRef}
            className="new-generated-image-ellipsis"
            onClick={handleEllipsisClick}
            aria-label="More options"
          >
            <img
              className="new-generated-image-icon"
              src={ellipsisIcon}
              alt="ellipsisIcon"
              role="button"
            />
          </button>
        </Dropdown>
      )}
    </div>
  )
}

export default memo(NewGeneratedImage)
